import { Component, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { UserStateService } from '../../core/services/user-state.service';
import { FormsModule } from '@angular/forms';
import { DatePipe, NgClass } from '@angular/common';
import { DataStoreService } from '../../core/services/data-store.service';
import { Appointment } from '../../core/models/appointment.model';
import { AppointmentStatus } from '../../core/enums/appointment-status.enum';
import { BookingService } from '../../core/services/booking.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { BookingSuccessModal } from '../../core/modals/booking-success-modal/booking-success.modal';
import { Router } from '@angular/router';
import { CreatePrescriptionModal } from '../../core/modals/create-prescription-modal/create-prescription.modal';
import { Medication } from '../../core/models/medication.model';
import { DocumentationService } from '../../core/services/documentation.service';
import { DoctorLetterModal } from '../../core/modals/doctor-letter/doctor-letter.modal';
import { SickNote } from '../../core/models/sick-note.model';
import { NotificationService } from '../../core/services/notification.service';
import { EmergencyContact } from '../../core/models/emergency-contact.model';
import { PatientService } from '../../core/services/patient.service';
import { MedicalRecord } from '../../core/models/patient-medical-record';
import { PatientFileSummary } from '../patient-file-summary/patient-file-summary';
import { PatientHealthHistory } from '../patient-health-history/patient-health-history';
import { DocumentUploadService } from '../../core/services/document-upload.service';
import { Document } from '../../core/models/document';
import { ConsultationPricingModal } from '../../core/modals/consultation-pricing-modal/consultation-pricing.modal';
import { ConsultationPricingItemDto } from '../../core/models/consultation-pricing-item.model';
import { AppointmentInvoiceDto } from '../../core/models/appointment-invoice.model';
import {
  InvoicePaymentSettlementDto,
  PaymentSource,
} from '../../core/models/invoice-payment-settlement.model';
import { SaveInvoiceSettlementsRequest } from '../../core/models/save-invoice-settlements-request.model';

@Component({
  selector: 'app-booking-view',
  templateUrl: './booking-view.html',
  standalone: true,
  imports: [FormsModule, NgClass, DatePipe, PatientFileSummary, PatientHealthHistory],
  styleUrls: ['./booking-view.scss'],
})
export class BookingViewComponent implements OnInit {
  private userStateService = inject(UserStateService);
  booking: Appointment | null | undefined;
  medications: Medication[] | null | undefined = [];
  invoiceItems: ConsultationPricingItemDto[] = [];
  invoiceTotal = 0;
  paymentRows: InvoicePaymentSettlementDto[] = [this.createPaymentRow()];
  healthHistory: MedicalRecord[] = [];
  emergencyContact: EmergencyContact | null = null;
  sickNote: SickNote | undefined;
  isLoading = false;

  selectedFiles: File[] = [];
  selectedDocumentType: string = 'OTHER';
  attachments: Document[] = [];

  readonly isReceptionUser = this.userStateService.isReception();
  readonly isDoctorUser = this.userStateService.isDoctor();
  readonly currentUserId = this.userStateService.userId();

  selectedFile: File | null = null;
  isSavingPaidAmount = false;
  isSavingDoctorNotes = false;

  constructor(
    private bookingService: BookingService,
    private patientService: PatientService,
    // private bookingDataStore: DataStoreService,
    private documentationService: DocumentationService,
    private documentUploadService: DocumentUploadService,
    private notificationService: NotificationService,
    private router: Router,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.booking = history.state['booking'];

    // this.booking = this.bookingDataStore.getBooking();
    this.medications =
      this.booking?.prescription?.medications !== null
        ? this.booking?.prescription?.medications
        : [];

    this.loadInvoiceItems();
    this.loadPatientDetails();
  }

  loadInvoiceItems(): void {
    const appointmentId = this.booking?.id;

    if (!appointmentId) {
      this.invoiceItems = [];
      this.invoiceTotal = 0;
      return;
    }

    this.bookingService.getAppointmentInvoice(appointmentId).subscribe({
      next: (invoice) => {
        this.applyInvoice(invoice);
      },
      error: (error) => {
        this.invoiceItems = [];
        this.invoiceTotal = 0;
        this.notificationService.error(
          error?.error?.message || error.message || 'Failed to load invoice details',
        );
      },
    });
  }

  private applyInvoice(invoice: AppointmentInvoiceDto | null): void {
    this.invoiceItems = invoice?.invoiceItems ?? [];
    this.invoiceTotal =
      invoice?.totalAmount ?? this.invoiceItems.reduce((total, item) => total + item.cost, 0);
    this.paymentRows = invoice?.settlements?.length
      ? invoice.settlements.map((settlement) => ({
          ...settlement,
          amountSettled: settlement.amountSettled ?? null,
          paidBy: settlement.paidBy,
        }))
      : [this.createPaymentRow()];
    this.cdr.markForCheck();
  }

  loadDocuments() {
    this.documentationService.getAppointmentDocuments(this.booking?.id).subscribe(
      (res) => {
        this.attachments = res;
      },
      (err) => {
        this.isLoading = false;
        this.notificationService.error(err.error.message);
      },
    );
  }

  loadPatientDetails() {
    this.patientService.getEmergencyContact(this.booking?.id).subscribe(
      (res) => {
        this.emergencyContact = res;
        this.isLoading = false;
      },
      (err) => {
        this.isLoading = false;
        this.notificationService.error(err.error.message);
      },
    );

    this.loadDocuments();

    this.patientService.getPatientMedicalRecords(this.booking?.patient.id).subscribe(
      (res) => {
        this.healthHistory = res;
        this.isLoading = false;
      },
      (error) => {
        this.isLoading = false;
        this.notificationService.error(error.error.message);
      },
    );
  }

  getStatusClass(status: AppointmentStatus): string {
    switch (status) {
      case AppointmentStatus.Confirmed:
        return 'status-scheduled';
      case AppointmentStatus.Pending:
        return 'status-pending';
      case AppointmentStatus.Cancelled:
        return 'status-cancelled';
      case AppointmentStatus.NoShow:
        return 'status-no-show';
      case AppointmentStatus.Completed:
        return 'status-completed';
      default:
        return 'status-scheduled';
    }
  }

  formatCurrency(value: number): string {
    return value.toFixed(2);
  }

  get totalAmountSettled(): number {
    return this.paymentRows.reduce((total, row) => total + Number(row.amountSettled || 0), 0);
  }

  get remainingInvoiceAmount(): number {
    return Math.max(this.invoiceTotal - this.totalAmountSettled, 0);
  }

  get canAddPaymentRow(): boolean {
    return this.invoiceTotal > 0 && this.totalAmountSettled < this.invoiceTotal;
  }

  get canCompleteBooking(): boolean {
    return this.isDoctorUser && this.booking?.doctor?.user?.id === this.currentUserId;
  }

  private getCurrentUserId(): string | null {
    return this.userStateService.userId();
  }

  addPaymentRow(): void {
    if (!this.canAddPaymentRow) {
      return;
    }

    this.paymentRows = [...this.paymentRows, this.createPaymentRow()];
  }

  removePaymentRow(index: number): void {
    if (this.paymentRows.length <= 1) {
      return;
    }

    this.paymentRows = this.paymentRows.filter((_, currentIndex) => currentIndex !== index);
  }

  getRowRemainingAmount(index: number, row: InvoicePaymentSettlementDto): number {
    if (row.remainingAmount !== null && row.remainingAmount !== undefined) {
      return row.remainingAmount;
    }

    return Math.max(
      this.invoiceTotal -
        this.paymentRows
          .slice(0, index + 1)
          .reduce((total, row) => total + (row.amountSettled ?? 0), 0),
      0,
    );
  }

  getPaymentSourceLabel(source: PaymentSource): string {
    return source === 'MEDICAL_AID' ? 'Medical aid' : 'Out of pocket';
  }

  saveDoctorNotes(): void {
    if (!this.booking?.id) {
      this.notificationService.error('Booking details are not available.');
      return;
    }

    this.isSavingDoctorNotes = true;

    this.bookingService.saveDoctorNotes(this.booking).subscribe({
      next: (updatedBooking) => {
        this.booking = updatedBooking;
        this.notificationService.success('Doctor notes saved successfully.');
        this.isSavingDoctorNotes = false;
      },
      error: (error) => {
        this.notificationService.error(
          error?.error?.message || error.message || 'Failed to save doctor notes',
        );
        this.isSavingDoctorNotes = false;
      },
    });
  }

  private createPaymentRow(): InvoicePaymentSettlementDto {
    return {
      amountSettled: null,
      paidBy: 'OUT_OF_POCKET',
      createdAt: new Date().toISOString(),
    };
  }

  completeBooking() {
    this.isLoading = true;
    this.bookingService.completeBooking(this.booking).subscribe(
      (res) => {
        this.isLoading = false;
        this.router.navigate(['/dashboard/booking-list']).then((r) => {});
      },
      (error) => {
        this.isLoading = false;
        this.notificationService.error(error.error.message);
      },
    );
  }

  noShowBooking() {
    this.isLoading = true;
    this.bookingService.markNoShow(this.booking).subscribe({
      next: () => {
        this.isLoading = false;
        this.router.navigate(['/dashboard/booking-list']);
      },
      error: (error) => {
        this.isLoading = false;
        this.notificationService.error(error?.error?.message || 'Failed to mark as no-show');
      },
    });
  }

  startBooking() {
    if (!!this.booking) {
      this.isLoading = true;
      this.bookingService.startBooking(this.booking?.id).subscribe(
        (res) => {
          this.isLoading = false;
          this.router.navigate(['/dashboard/booking-list']).then((r) => {});
        },
        (error) => {
          this.isLoading = false;
          this.notificationService.error(error.error.message);
        },
      );
    }
  }

  cancelBooking() {
    this.isLoading = true;
    this.bookingService.cancelBooking(this.booking).subscribe(
      (res) => {
        this.isLoading = false;
        this.router.navigate(['/dashboard/booking-list']).then((r) => {});
      },
      (error) => {
        this.isLoading = false;
        this.notificationService.error(error.error.message);
      },
    );
  }

  confirmBooking() {
    if (!!this.booking) {
      this.booking.status = AppointmentStatus.Confirmed;
    }

    this.isLoading = true;
    this.bookingService.confirmBooking(this.booking).subscribe(
      (res) => {
        this.isLoading = false;
        console.log('Booking confirmed:', this.booking);
        const dialogConfig = new MatDialogConfig();

        // Optional: Configure dialog properties
        dialogConfig.disableClose = true; // Prevents closing by clicking outside or pressing Escape
        dialogConfig.autoFocus = true; // Focuses on the first form field/focusable element
        dialogConfig.width = '450px'; // Set a specific width
        dialogConfig.data = {
          appointment: this.booking,
        };

        const dialogRef = this.dialog.open(BookingSuccessModal, dialogConfig);
        dialogRef.afterClosed().subscribe((result) => {
          this.router.navigate(['/dashboard/booking-list']).then((r) => {});
        });
      },
      (error) => {
        this.isLoading = false;
        this.notificationService.error(error.error.message);
      },
    );
  }

  createPrescription() {
    const dialogConfig = new MatDialogConfig();

    // Optional: Configure dialog properties
    dialogConfig.disableClose = false; // Prevents closing by clicking outside or pressing Escape
    dialogConfig.autoFocus = true; // Focuses on the first form field/focusable element
    dialogConfig.width = '1000px'; // Set a specific width
    dialogConfig.maxHeight = '90vh'; // Ensure modal fits in viewport

    const dialogRef = this.dialog.open(CreatePrescriptionModal, dialogConfig);
    dialogRef.afterClosed().subscribe((result) => {
      this.medications = result;

      if (!!this.medications && this.medications.length > 0) {
        this.isLoading = true;
        this.bookingService
          .savePrescriptionMedication(this.medications, this.booking?.id)
          .subscribe({
            next: (prescription) => {
              if (this.booking) {
                this.booking.prescription = prescription;
                this.booking.showPrescriptionDocumentOption = true;
              }
              this.medications = prescription?.medications?.length
                ? prescription.medications
                : this.medications;
              this.isLoading = false;
              this.notificationService.success('Prescription created successfully.');

              if (prescription?.id) {
                this.documentationService.downloadPrescriptionLetter(prescription.id).subscribe({
                  next: (file) => {
                    const fileURL = window.URL.createObjectURL(file);
                    window.open(fileURL);
                  },
                  error: (error) => {
                    this.notificationService.error(
                      error?.error?.message || error.message || 'Failed to download prescription',
                    );
                  },
                });
              }
            },
            error: (error) => {
              this.medications = [];
              this.notificationService.error(
                error?.error?.message || error.message || 'Failed to save prescription',
              );
              this.isLoading = false;
            },
          });
      }
    });
  }

  writeSickNote() {
    if (!!this.booking?.patientSickNote) {
      this.isLoading = true;
      this.documentationService.downloadSickNote(this.booking?.patientSickNote.id).subscribe(
        (res) => {
          this.isLoading = false;
          const fileURL = window.URL.createObjectURL(res);
          window.open(fileURL); // opens in new tab
        },
        (error) => {
          this.isLoading = false;
          this.notificationService.error(error.error.message);
        },
      );
      return;
    }

    const dialogConfig = new MatDialogConfig();

    // Optional: Configure dialog properties
    dialogConfig.disableClose = false; // Prevents closing by clicking outside or pressing Escape
    dialogConfig.autoFocus = true; // Focuses on the first form field/focusable element
    dialogConfig.width = '500px'; // Set a specific width

    const dialogRef = this.dialog.open(DoctorLetterModal, dialogConfig);
    dialogRef.afterClosed().subscribe((result) => {
      this.sickNote = result;

      if (!!this.sickNote) {
        this.sickNote.appointmentId = <string>this.booking?.id;

        this.bookingService.writeSickNote(this.sickNote).subscribe(
          (res) => {
            this.documentationService.downloadSickNote(res.id).subscribe((res) => {
              const fileURL = window.URL.createObjectURL(res);
              window.open(fileURL); // opens in new tab
            });
          },
          (error) => {
            this.isLoading = false;
            this.notificationService.error(error.error.message);
            this.medications = [];
          },
        );
      }
    });
  }

  openConsultationPricingModal(): void {
    if (!this.booking?.id) {
      this.notificationService.error('Booking details are not available.');
      return;
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '960px';
    dialogConfig.maxWidth = '96vw';
    dialogConfig.data = {
      appointmentId: this.booking.id,
      patientName:
        `${this.booking.patient?.firstName ?? ''} ${this.booking.patient?.lastName ?? ''}`.trim(),
    };

    const dialogRef = this.dialog.open(ConsultationPricingModal, dialogConfig);
    
    dialogRef.afterClosed().subscribe((result) => {
      // Refresh the invoice items whenever the modal closes, in case an invoice was generated.
      // Usually result is truthy if the modal successfully completed its action.
      if (result) {
        this.loadInvoiceItems();
      } else {
        // As a fallback, always try loading the invoice just in case it was generated
        // but the modal didn't pass a result payload.
        this.loadInvoiceItems();
      }
    });
  }

  savePaidAmount(): void {
    if (!this.booking?.id) {
      this.notificationService.error('Booking details are not available.');
      return;
    }

    const paidAmount = this.totalAmountSettled;

    if (paidAmount <= 0) {
      this.notificationService.error('Enter at least one settled amount before saving.');
      return;
    }

    if (paidAmount > this.invoiceTotal) {
      this.notificationService.error(
        'Total settled amount cannot be greater than the invoice total.',
      );
      return;
    }

    this.isSavingPaidAmount = true;

    const request: SaveInvoiceSettlementsRequest = {
      appointmentId: this.booking.id,
      settlements: this.paymentRows
        .map((row) => ({
          id: row.id,
          amountSettled: this.normalizeSettlementAmount(row.amountSettled),
          paidBy: row.paidBy,
        }))
        .filter(
          (row): row is { id: string | undefined; amountSettled: number; paidBy: PaymentSource } =>
            row.amountSettled !== null && row.amountSettled > 0,
        ),
    };

    this.bookingService.saveInvoiceSettlements(request).subscribe({
      next: (updatedInvoice) => {
        this.applyInvoice(updatedInvoice);
        this.notificationService.success('Invoice settlements saved successfully.');
        this.isSavingPaidAmount = false;
      },
      error: (error) => {
        this.notificationService.error(
          error?.error?.message || error.message || 'Failed to save invoice settlements',
        );
        this.isSavingPaidAmount = false;
      },
    });
  }

  private normalizeSettlementAmount(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsedAmount = typeof value === 'number' ? value : Number(value);
    return Number.isNaN(parsedAmount) ? NaN : parsedAmount;
  }

  navigateToChatScreen() {
    this.router.navigate(['dashboard/chat-messages'], {
      state: { bookingId: this.booking?.id },
    });
  }

  downPrescription() {
    this.isLoading = true;
    this.documentationService.downloadPrescriptionLetter(this.booking?.prescription.id).subscribe(
      (res) => {
        this.isLoading = false;
        console.log('file downloded');

        const fileURL = window.URL.createObjectURL(res);
        window.open(fileURL); // opens in new tab
      },
      () => (this.isLoading = false),
    );
  }

  generateDoctorLetter() {
    console.log('Generate doctor letter for');
    // trigger PDF/doc generation
  }

  writePrescription() {
    console.log('Write prescription for:');
    // open prescription form/modal
  }

  sendPatientMessage() {
    console.log('Send message to:');
    // open messaging modal
  }

  onFilesSelected(event: any): void {
    const files: FileList = event.target.files;

    const MAX_SIZE = 10 * 1024 * 1024; // 10MB per file (adjust as needed)

    const validFiles: File[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (file.size > MAX_SIZE) {
        this.notificationService.error(`File too large: ${file.name} (Max 10MB allowed)`);
        continue;
      }

      validFiles.push(file);
    }

    this.selectedFiles = validFiles;
    // Keep selectedFile in sync so the upload button guard and uploadAttachment() work correctly
    this.selectedFile = validFiles.length > 0 ? validFiles[0] : null;
  }

  uploadAttachment(): void {
    if (!this.selectedFile || !this.booking?.id) return;

    const formData = new FormData();

    formData.append('file', this.selectedFile);
    formData.append('bookingId', this.booking.id);
    formData.append('documentType', this.selectedDocumentType);
    formData.append('appointmentId', this.booking.id);

    this.documentUploadService.uploadDocument(formData, null).subscribe({
      next: (res) => {
        this.loadDocuments();

        // reset after upload
        this.selectedFile = null;
        this.selectedFiles = [];
        this.selectedDocumentType = 'OTHER';
      },
      error: (err) => {
        console.error(err);
        this.notificationService.error(err.error.message || 'Upload failed');
      },
    });
  }

  downloadFile(file: any): void {
    this.documentUploadService.downloadDocument(this.booking?.id, file.id).subscribe((blob) => {
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = file.fileName;
      a.click();

      window.URL.revokeObjectURL(url);
    });
  }

  deleteFile(fileId: string): void {
    this.documentUploadService.deleteDocument(this.booking?.id, fileId).subscribe({
      next: () => {
        this.loadDocuments();
      },
      error: (err) => {
        console.error(err);
        this.notificationService.error(err.error.message || 'Delete failed');
      },
    });
  }

  protected readonly AppointmentStatus = AppointmentStatus;
}
